import z from "zod";

import { createQueryRequestDefinition } from "~/lib/network/definitions/create";

import {
  bookmarkedVideoFeedResponseSchema,
  customActorTypeSchema,
  mirageActorLookupResponseSchema,
  mirageActorsFeedResponseSchema,
  mirageAudioFeedResponseSchema,
  mirageAudioLookupResponseSchema,
  mirageVideoFeedResponseSchema,
  mirageVideoLookupResponseSchema,
  voiceMetadataResponseSchema,
} from "./schemas";

export const fetchMirageActorsQuery = createQueryRequestDefinition({
  queryKey: ["oasis", "mirage", "actors", "feed"],
  host: "cap-srv",
  method: "POST",
  path: "oasis/mirage-actors/feed",
  paramsSchema: z.object({
    afterCursor: z.string().nullable().optional().default(null),
    pageSize: z.number().max(50).optional().default(20),
    filters: z.object({
      userId: z.string(),
      type: customActorTypeSchema.optional(),
      gender: z.enum(["male", "female"]).optional(),
    }),
  }),
  responseSchema: mirageActorsFeedResponseSchema,
}).withPagination({
  getNextPageParam(lastPage) {
    return lastPage.actors.pageInfo.hasNextPage ? lastPage.actors.pageInfo.endCursor : undefined;
  },
  mapPaginationParamsToQueryParams({ pageParam }) {
    return {
      ...(pageParam ? { afterCursor: pageParam } : {}),
      pageSize: 20,
    };
  },
});

export const lookupMirageActorQuery = createQueryRequestDefinition({
  queryKey: ["oasis", "mirage", "actors", "lookup"],
  host: "cap-srv",
  method: "GET",
  path: "oasis/mirage-actors/lookup/:actorId",
  responseSchema: mirageActorLookupResponseSchema,
});

export const fetchMirageVideosQuery = createQueryRequestDefinition({
  queryKey: ["oasis", "mirage", "videos", "feed"],
  host: "cap-srv",
  method: "POST",
  path: "oasis/mirage-videos/feed",
  paramsSchema: z.object({
    afterCursor: z.string().nullable().optional().default(null),
    pageSize: z.number().max(20).optional().default(20),
    filters: z
      .object({
        orgId: z.string().optional(),
        userId: z.string().optional(),
        excludeEmployees: z.boolean().optional(),
        caller: z.string().optional(),
        twinsOnly: z.boolean().optional(),
        referenceMediaType: z.enum(["image", "video"]).optional(),
      })
      .optional(),
  }),
  responseSchema: mirageVideoFeedResponseSchema,
}).withPagination({
  getNextPageParam(lastPage) {
    return lastPage.videos.pageInfo.hasNextPage ? lastPage.videos.pageInfo.endCursor : undefined;
  },
  mapPaginationParamsToQueryParams({ pageParam }) {
    return {
      ...(pageParam ? { afterCursor: pageParam } : {}),
      pageSize: 20,
    };
  },
});

export const lookupMirageVideoByIdQuery = createQueryRequestDefinition({
  queryKey: ["oasis", "mirage", "videos", "lookup"],
  host: "cap-srv",
  method: "GET",
  path: "oasis/mirage-videos/lookup/:videoId",
  responseSchema: mirageVideoLookupResponseSchema,
});

export const fetchBookmarkedVideosQuery = createQueryRequestDefinition({
  queryKey: ["oasis", "bookmarks", "videos", "feed"],
  host: "cap-srv",
  method: "POST",
  path: "oasis/bookmarks/videos/feed",
  paramsSchema: z.object({
    afterCursor: z.string().nullable().optional().default(null),
    pageSize: z.number().min(1).max(100).optional().default(20),
  }),
  responseSchema: bookmarkedVideoFeedResponseSchema,
}).withPagination({
  getNextPageParam(lastPage) {
    return lastPage.videos.pageInfo.hasNextPage ? lastPage.videos.pageInfo.endCursor : undefined;
  },
  mapPaginationParamsToQueryParams({ pageParam }) {
    return {
      ...(pageParam ? { afterCursor: pageParam } : {}),
      pageSize: 20,
    };
  },
});

export const fetchMirageAudioQuery = createQueryRequestDefinition({
  queryKey: ["oasis", "mirage", "audio", "feed"],
  host: "cap-srv",
  method: "POST",
  path: "oasis/mirage-audio/feed",
  paramsSchema: z.object({
    afterCursor: z.string().nullable().optional().default(null),
    pageSize: z.number().max(20).optional().default(20),
    filters: z
      .object({
        userId: z.string().optional(),
        tags: z.array(z.string()).optional(),
      })
      .optional(),
  }),
  responseSchema: mirageAudioFeedResponseSchema,
}).withPagination({
  getNextPageParam(lastPage) {
    return lastPage.audio.pageInfo.hasNextPage ? lastPage.audio.pageInfo.endCursor : undefined;
  },
  mapPaginationParamsToQueryParams({ pageParam }) {
    return {
      ...(pageParam ? { afterCursor: pageParam } : {}),
      pageSize: 20,
    };
  },
});

export const lookupMirageAudioByIdQuery = createQueryRequestDefinition({
  queryKey: ["oasis", "mirage", "audio", "lookup"],
  host: "cap-srv",
  method: "GET",
  path: "oasis/mirage-audio/lookup/:audioId",
  responseSchema: mirageAudioLookupResponseSchema,
});

export const queryVoiceIdMetadataQuery = createQueryRequestDefinition({
  queryKey: ["oasis", "mirage", "voice-metadata"],
  host: "cap-srv",
  method: "GET",
  path: "oasis/mirage-audio/query-voice-id-metadata/:voiceId",
  responseSchema: voiceMetadataResponseSchema,
});
